import React from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Switch } from "@/components/ui/switch";
import Text from "@/components/ui/text";
import {
  useContractSubmissionSettings,
  useSaveStripeSource,
  useStripeConnectionSettings
} from "@/entities/stripeCheckout/hooks";
import { webFormErrorDetail } from "@/entities/webForm/hooks";
import { CompanyIdentificationSection } from "@/pages/settings/components/integrations/stripeCheckout/CompanyIdentificationSection";
import { ContractDetailsSection } from "@/pages/settings/components/integrations/stripeCheckout/ContractDetailsSection";
import { FailureAlertsSection } from "@/pages/settings/components/integrations/stripeCheckout/FailureAlertsSection";
import { FulfillmentSection } from "@/pages/settings/components/integrations/stripeCheckout/FulfillmentSection";
import { PricesSection } from "@/pages/settings/components/integrations/stripeCheckout/PricesSection";
import { StripeConnectionSection } from "@/pages/settings/components/integrations/stripeCheckout/StripeConnectionSection";
import {
  STRIPE_CHECKOUT_PREFIX as PREFIX,
  sourceDraft,
  sourceUpdate
} from "@/pages/settings/components/integrations/stripeCheckout/stripeCheckoutOptions";
import Section from "@/pages/settings/components/sections/Section";
import { useCanAccessAdminSettings } from "@/pages/settings/hooks";

const StripeCheckoutControls: React.FC = () => {
  const { t } = useTranslation();
  const connectionQuery = useStripeConnectionSettings();
  const submissionQuery = useContractSubmissionSettings();
  const saveSource = useSaveStripeSource();
  const submission = submissionQuery.data;
  const source = submission?.sources.stripe;
  const isLoading = submissionQuery.isLoading;

  const sectionSubtitle = (key: string) =>
    submissionQuery.isError ? t(`${PREFIX}.loadError`) : t(key);

  const handleToggle = (enabled: boolean) => {
    if (!source) return;
    saveSource.mutate(
      { data: sourceUpdate(enabled, sourceDraft(source)) },
      {
        onError: (error) =>
          toast.error(webFormErrorDetail(error) ?? t(`${PREFIX}.status.error`))
      }
    );
  };

  return (
    <div
      className="flex w-full flex-col gap-12"
      data-test="stripe-checkout-settings"
    >
      <Section
        isLoading={isLoading}
        subTitle={
          submissionQuery.isError ? t(`${PREFIX}.loadError`) : undefined
        }
        items={[
          {
            title: t(`${PREFIX}.status.enabled.title`),
            subTitle: t(`${PREFIX}.status.enabled.subtitle`),
            customAction: (
              <Switch
                data-test="stripe-checkout-enabled"
                aria-label={t(`${PREFIX}.status.enabled.title`)}
                checked={source?.enabled ?? false}
                disabled={!source || saveSource.isPending}
                onCheckedChange={handleToggle}
              />
            )
          }
        ]}
      />
      <StripeConnectionSection
        settings={connectionQuery.data}
        isLoading={connectionQuery.isLoading}
        isError={connectionQuery.isError}
      />
      <CompanyIdentificationSection
        source={source}
        isLoading={isLoading}
        subTitle={sectionSubtitle(`${PREFIX}.company.subtitle`)}
        isBusy={saveSource.isPending}
      />
      <PricesSection
        mapping={source?.mapping}
        isLoading={isLoading}
        subTitle={sectionSubtitle(`${PREFIX}.prices.subtitle`)}
        isBusy={false}
      />
      <ContractDetailsSection
        mapping={source?.mapping}
        isLoading={isLoading}
        subTitle={sectionSubtitle(`${PREFIX}.contract.subtitle`)}
        isBusy={false}
      />
      <FulfillmentSection
        task={submission?.fulfillmentTask}
        isLoading={isLoading}
        subTitle={sectionSubtitle(`${PREFIX}.fulfillment.subtitle`)}
        isBusy={false}
      />
      <FailureAlertsSection
        settings={submission}
        isLoading={isLoading}
        subTitle={sectionSubtitle(`${PREFIX}.alerts.subtitle`)}
        isBusy={false}
      />
    </div>
  );
};

export const StripeCheckoutSettings: React.FC = () => {
  const { t } = useTranslation();
  const isSettingsAdmin = useCanAccessAdminSettings();

  // Every Stripe Checkout route is admin-only, so nobody else gets controls or
  // makes a request that would be refused.
  if (!isSettingsAdmin) {
    return (
      <Text
        variant="bodySecondary"
        data-test="stripe-checkout-settings-admin-only"
      >
        {t(`${PREFIX}.adminOnly`)}
      </Text>
    );
  }
  return <StripeCheckoutControls />;
};
